import React, { useState, useEffect, useMemo, memo } from 'react';
import { Card, Row, Col, Typography } from 'antd';
import { UserOutlined, DollarOutlined, RiseOutlined } from '@ant-design/icons';
import { statsAPI } from '../services/api';
import { Line } from 'react-chartjs-2';
import { PageHeader, StatCard, LoadingOverlay, EmptyState } from '../components/ui';
import { lineChartOptions, CHART_COLORS } from '../styles/chart-config';
import { formatFlowAmount, scaleFlowAmount } from '../utils/displayMoney';

const { Text } = Typography;

/**
 * 数据统计页面
 */
const Statistics = memo(function Statistics() {
  const [loading, setLoading] = useState(true);
  const [paymentStats, setPaymentStats] = useState(null);

  // 加载统计数据
  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await statsAPI.getPaymentStats();
      setPaymentStats(response.data.data);
    } catch (error) {
      console.error('Load stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  // 图表数据
  const chartData = useMemo(() => {
    if (!paymentStats?.trend) return null;
    return {
      labels: paymentStats.trend.map(item => item.date),
      datasets: [{
        label: '每日收入',
        data: paymentStats.trend.map(item => scaleFlowAmount(item.total)),
        borderColor: CHART_COLORS.pink,
        backgroundColor: CHART_COLORS.pinkAlpha,
        tension: 0.3,
        fill: true
      }]
    };
  }, [paymentStats]);

  // 统计卡片数据
  const statCards = useMemo(() => {
    const avgValue = (paymentStats?.today?.total || 0) / (paymentStats?.today?.count || 1) || 0;
    return [
      {
        label: '今日支付次数',
        value: paymentStats?.today?.count || 0,
        icon: <UserOutlined />,
        color: '#2563eb'
      },
      {
        label: '今日收入',
        value: formatFlowAmount(paymentStats?.today?.total),
        icon: <DollarOutlined />,
        color: '#ea580c',
        suffix: '元'
      },
      {
        label: '平均收入',
        value: formatFlowAmount(avgValue),
        icon: <RiseOutlined />,
        color: '#16a34a',
        suffix: '元'
      }
    ];
  }, [paymentStats]);

  if (loading) {
    return <LoadingOverlay tip="加载统计数据..." />;
  }

  return (
    <div className="page-container">
      <PageHeader
        title="数据统计"
        description="支付频次、收入和近 7 日趋势分析"
        tags={[
          { text: '支付概览', color: 'blue' },
          { text: '趋势分析', color: 'default' }
        ]}
      />

      {/* 统计卡片 */}
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        {statCards.map((card, index) => (
          <Col key={index} xs={24} sm={8}>
            <StatCard {...card} />
          </Col>
        ))}
      </Row>

      {/* 收入趋势图表 */}
      <Card
        className="card-section"
        title="近 7 日收入趋势"
        extra={<Text type="secondary">按天查看收入变化</Text>}
      >
        <div className="chart-panel" style={{ height: 320 }}>
          {chartData ? (
            <Line data={chartData} options={lineChartOptions('近 7 日收入趋势')} />
          ) : (
            <EmptyState
              title="暂无统计数据"
              description="当前没有可展示的收入趋势数据"
            />
          )}
        </div>
      </Card>
    </div>
  );
});

export default Statistics;
